<template>
    <div class="news-slider" v-if="news.length">
        <div class="section-lastNews">
            <div class="section-lastNews__header">
                <div class="title">
                    <i class="icon-latest-news"></i>
                    <div class="title__text">
                        <h2>{{ $t('front.news.slider.title') }}</h2>
                        <p>{{ $t('front.news.slider.description') }}</p>
                    </div>
                </div>
                <div class="slider-nav">
                    <router-link :to="seeAllLink">
                        {{ $t('front.news.button.see_all') }}
                        <svg width="7" height="12" viewBox="0 0 7 12" fill="none"
                             xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 11L6 6L1 1" stroke="#926D20"></path>
                        </svg>
                    </router-link>
                    <v-btn v-on:click="swiperPrev" class="prev" id="button-prev-relacionados">
                        <svg width="11" height="10" viewBox="0 0 11 10" fill="none"
                             xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 1L2 5L6 9" stroke="white" stroke-width="1.5"/>
                            <path d="M2.5 5H10.5" stroke="white" stroke-width="1.5"/>
                        </svg>
                    </v-btn>
                    <v-btn v-on:click="swiperNext" class="next" id="button-next-relacionados">
                        <svg width="11" height="10" viewBox="0 0 11 10" fill="none"
                             xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 1L9 5L5 9" stroke="white" stroke-width="1.5"/>
                            <path d="M8.5 5H0.5" stroke="white" stroke-width="1.5"/>
                        </svg>
                    </v-btn>
                </div>
            </div>
            <div class="section-lastNews__content">
                <swiper ref="slider" class="section-lastNews__slider" :options="settings" v-if="news.length">
                    <swiper-slide class="item load" v-for="item in news"
                                  :key="item.id">
                        <div class="item__box">
                            <div class="item__image">
                                <router-link class="item__image-category" :to="item.categoryPageUrl"
                                             v-text="item.categoryTitle"/>
                                <router-link class="link" :to="item.pageUrl">
                                    <picture>
                                        <source
                                            :data-srcset="item[isDeviceMobile ? 'mobileImage' : 'desktopImage'][sourceType]"
                                            :type="`image/${sourceType}`"
                                            v-for="(sourceType, index) in item[isDeviceMobile ? 'mobileImage' : 'desktopImage'].sourcesTypes"
                                            :key="index">
                                        <img :data-src="item[isDeviceMobile ? 'mobileImage' : 'desktopImage'].jpg"
                                             :alt="item.title"/>
                                    </picture>
                                </router-link>
                            </div>
                            <div class="item__content">
                                <h2 v-text="item.title"/>
                                <p v-text="item.shortDescription"/>
                            </div>
                        </div>
                    </swiper-slide>
                </swiper>
            </div>
        </div>
    </div>
</template>

<script>
import {slidesCount} from "../../../../../config/slider";

import {Swiper, SwiperSlide} from "vue-awesome-swiper";
import apiData from "../../../../mixins/apiData";

export default {
    name: "NewsSlider",

    components: {
        Swiper,
        SwiperSlide,
    },

    mixins: [apiData],

    data: function () {
        return {
            updateTimeout: undefined,
            settings: {
                init: true,
                loop: true,
                slidesPerView: 1,
                watchSlidesProgress: true,
                watchSlidesVisibility: true,
                preloadImages: false,
                direction: 'horizontal',
                spaceBetween: 0,
                speed: 500,
                threshold: 5,
                navigation: {
                    nextEl: '#button-next-relacionados',
                    prevEl: '#button-prev-relacionados',
                },
                breakpoints: {
                    480: {
                        slidesPerView: 2,
                        spaceBetween: 0,
                    },
                    768: {
                        slidesPerView: 3,
                        spaceBetween: 0,
                    },
                    1024: {
                        slidesPerView: 4,
                        spaceBetween: 0,
                    },
                }
            },
        }
    },

    serverPrefetch() {
        return this.fetchData();
    },

    mounted() {
        this.fetchData().then(() => this.swiper && setTimeout(() => {
            this.loadCurrentImages();

            this.swiper.on('beforeTransitionStart', this.loadCurrentImages);
        }, 100));

        window.addEventListener('resize', this.setUpdateTimeout, false);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.setUpdateTimeout, false);
        this.clearUpdateTimeout();
    },

    computed: {
        isDeviceMobile() {
            return this.$store.state.isDeviceMobile;
        },

        isWebpSupported() {
            return this.$store.state.isWebpSupported;
        },

        locale() {
            return this.$store.state.locale;
        },

        swiper() {
            return this.$refs.slider && this.$refs.slider.$swiper;
        },

        news() {
            return this.$store.state.sliderNews;
        },

        seeAllLink() {
            let languageUrlPrefix = this.$store.getters.getLanguageUrlPrefix();
            return `${languageUrlPrefix}/blog`;
        },

        showingSlidesCount() {
            if (window.innerWidth < 480) {
                return 1;
            } else if (window.innerWidth < 768) {
                return 2;
            } else if (window.innerWidth < 1024) {
                return 3;
            } else {
                return 4;
            }
        }
    },

    methods: {
        fetchData() {
            return this.$store.dispatch('FETCH_SLIDER_NEWS', this.createApiData({
                count: slidesCount.news,
            }))
        },

        setUpdateTimeout() {
            if (this.updateTimeout) {
                this.clearUpdateTimeout();
            }

            this.updateTimeout = setTimeout(this.updateSlider, 400);
        },

        clearUpdateTimeout() {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = null;
        },

        updateSlider() {
            if (this.swiper) {
                this.swiper.update();
                setTimeout(() => this.loadCurrentImages(), 100);
            }
        },

        swiperNext() {
            this.swiper.slideNext();
        },

        swiperPrev() {
            this.swiper.slidePrev();
        },

        loadCurrentImages() {
            let activeSlideIndex = this.swiper.activeIndex;

            for (let i = -1; i <= this.showingSlidesCount; i++) {
                let slide = this.swiper.slides[activeSlideIndex + i];

                if (slide && !slide.imageLoaded) {
                    this.loadImage(slide);

                    slide.imageLoaded = true;
                }
            }
        },

        loadImage(slide) {
            let activeSlideImage = slide.getElementsByTagName('img')[0];
            let activeSlideSources = slide.getElementsByTagName('source');

            activeSlideImage.setAttribute('src', activeSlideImage.dataset.src);

            activeSlideSources && Array.prototype.forEach.call(activeSlideSources, activeSlideSource => activeSlideSource.setAttribute('srcset', activeSlideSource.dataset.srcset));

            slide.imageLoaded = true;
        },
    },
}
</script>


<style lang="sass">
@import "sass/components/pages/parts/itemSets/NewsSlider"
</style>